import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {
  CameraQrScannerPane,
  isQrCameraScannerSupported,
} from "@bw/shared/components";
import type { Schema } from "@bw/shared/types/generated";
import { useCallback, useEffect, useRef, useState } from "react";

import * as tournamentApi from "@/libs/tournamentApi";

type Resolution = Schema["TournamentScoreSheetResolution"];

/**
 * Resolves a scanned or manually entered opaque reference entirely in memory.
 * The route remains unchanged: neither the reference nor a match ID is placed
 * into a URL or persisted by the browser.
 */
export function ScoreSheetResolverDialog({
  onOpenScorePanel,
}: {
  onOpenScorePanel: (resolution: Resolution) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"scan" | "manual">(
    isQrCameraScannerSupported() ? "scan" : "manual",
  );
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannerActive, setScannerActive] = useState(true);
  const operationGeneration = useRef(0);
  const busyRef = useRef(false);
  const scanSupported = isQrCameraScannerSupported();

  const reset = () => {
    busyRef.current = false;
    setBusy(false);
    setReference("");
    setError(null);
    setScannerActive(true);
  };

  const close = () => {
    operationGeneration.current += 1;
    setOpen(false);
    reset();
  };

  useEffect(
    () => () => {
      operationGeneration.current += 1;
      busyRef.current = false;
    },
    [],
  );

  const resolveAndOpen = useCallback(
    async (value: string) => {
      if (busyRef.current) return;
      const opaqueReference = value.trim();
      if (!opaqueReference) {
        setError("Enter a game-sheet reference.");
        return;
      }
      busyRef.current = true;
      setBusy(true);
      setError(null);
      const generation = ++operationGeneration.current;
      try {
        const target = await tournamentApi.resolveTournamentScoreSheet({
          reference: opaqueReference,
        });
        if (generation !== operationGeneration.current) return;
        setReference("");
        setScannerActive(false);
        await onOpenScorePanel(target);
        if (generation === operationGeneration.current) close();
      } catch {
        if (generation === operationGeneration.current) {
          setError(
            "This game sheet cannot be opened. Print a current sheet or check access.",
          );
          setScannerActive(false);
        }
      } finally {
        if (generation === operationGeneration.current) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    },
    [onOpenScorePanel],
  );

  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
        Scan game sheet
      </Button>
      <Dialog
        open={open}
        onClose={busy ? undefined : close}
        fullWidth
        maxWidth="sm"
        aria-labelledby="score-sheet-resolver-title"
      >
        <DialogTitle id="score-sheet-resolver-title">
          Open score sheet
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <DialogContentText>
              Scan the QR code or enter its printed reference. Opening a sheet
              does not record or finalize a score.
            </DialogContentText>
            {scanSupported ? (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant={mode === "scan" ? "contained" : "outlined"}
                  aria-pressed={mode === "scan"}
                  onClick={() => setMode("scan")}
                >
                  Scan
                </Button>
                <Button
                  size="small"
                  variant={mode === "manual" ? "contained" : "outlined"}
                  aria-pressed={mode === "manual"}
                  onClick={() => setMode("manual")}
                >
                  Enter reference
                </Button>
              </Stack>
            ) : null}
            {mode === "scan" && scanSupported ? (
              scannerActive ? (
                <CameraQrScannerPane
                  active={open && scannerActive}
                  ariaLabel="Camera preview for scanning a tournament game-sheet QR code"
                  onDetected={(value) => void resolveAndOpen(value)}
                />
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setError(null);
                    setScannerActive(true);
                  }}
                >
                  Scan another sheet
                </Button>
              )
            ) : null}
            {mode === "manual" || !scanSupported ? (
              <TextField
                label="Game-sheet reference"
                value={reference}
                onChange={(event) => {
                  setReference(event.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void resolveAndOpen(reference);
                  }
                }}
                autoComplete="off"
                autoFocus
                fullWidth
                size="small"
              />
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={close} disabled={busy}>
            Cancel
          </Button>
          {mode === "manual" || !scanSupported ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => void resolveAndOpen(reference)}
              disabled={busy}
            >
              {busy ? "Opening…" : "Open umpire score panel"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}
